import {isFeatureEnabled} from 'feature-flags';
import {Button, FormControl, FormField, IconButton} from 'platform/components';
import {Box, HStack, Show, Space, VStack} from 'platform/foundation';

import {useEffect} from 'react';
import {useFieldArray, UseFormReturn} from 'react-hook-form';

import {isNil} from 'ramda';
import {isNotNil, isString} from 'ramda-adjunct';

import {useGetEmployeeMechanicsQuery} from '@dms/api/customer';
import {useGetMetadaServiceCaseQuery} from '@dms/api/workshop';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';

import {suffixTestId, TestIdProps} from 'shared/web';

import {EditItemFormType} from '../types/editItemFormSchema';

const DEFAULT_RATIO = 100;

interface EditItemMechanicsProps extends TestIdProps {
  control: FormControl<EditItemFormType>;
  formApi: UseFormReturn<EditItemFormType>;
  serviceCaseId: string;
  isDisabled?: boolean;
}

export function EditItemMechanics(props: EditItemMechanicsProps) {
  const {data: serviceCase} = useGetMetadaServiceCaseQuery(
    {serviceCaseId: props.serviceCaseId},
    {skip: isNil(props.serviceCaseId)}
  );
  const {mechanicOptions} = useGetEmployeeMechanicsQuery(
    {
      authorizationProfileId: serviceCase?.authorizationProfileId ?? '',
      isActive: true,
      withToOffer: true,
    },
    {
      selectFromResult: ({data}) => ({
        mechanicOptions: data?.map((mechanic) => ({
          label: mechanic?.name ?? '',
          value: mechanic?.employeeId ?? '',
        })),
      }),
      skip:
        isNil(serviceCase?.authorizationProfileId) ||
        !isFeatureEnabled(featureFlags.ACL_EMPLOYEE_MANAGEMENT),
    }
  );

  const {fields, append, remove, update} = useFieldArray({
    control: props.control,
    name: 'assignMechanics',
    keyName: 'rowId',
  });

  useEffect(() => {
    if (fields.length === 1) {
      const totalQuantity = props.formApi.getValues('quantity');

      update(0, {...fields[0], amount: totalQuantity ?? 0, ratio: DEFAULT_RATIO});
    }
  }, [fields.length, props.formApi, update]);

  const handleMechanicChange = (index: number) => (value: string | number | string[] | null) => {
    if (!isString(value)) {
      return;
    }

    update(index, {
      ...fields[index],
      id: value,
    });
  };

  const handleQuantityChange = (index: number) => (value: number | null) => {
    const totalQuantity = props.formApi.getValues('quantity') ?? 0;
    const newRatio = isNotNil(value) ? Math.round((value / totalQuantity) * 100) : 0;

    props.formApi.setValue(`assignMechanics.${index}.amount`, value ?? 0);
    props.formApi.setValue(`assignMechanics.${index}.ratio`, newRatio);
  };

  const handleRatioChange = (index: number) => (value: number | null) => {
    const totalQuantity = props.formApi.getValues('quantity') ?? 0;
    const newAmount = isNotNil(value) ? Math.round(value * totalQuantity) / 100 : 0;

    props.formApi.setValue(`assignMechanics.${index}.ratio`, value ?? 0);
    props.formApi.setValue(`assignMechanics.${index}.amount`, newAmount);
  };

  return (
    <VStack spacing={4} align="flex-start">
      {fields.map((field, index) => (
        <VStack key={field.id} spacing={4} width="100%">
          <HStack spacing={4}>
            <Box flex={1}>
              <FormField
                control={props.control}
                name={`assignMechanics.${index}.id`}
                type="choice"
                label={index === 0 ? i18n.t('entity.order.labels.mechanic') : undefined}
                filterOption={(option) =>
                  props.formApi
                    .watch('assignMechanics')
                    ?.every((mechanic) => mechanic.id !== option.value) ?? true
                }
                options={mechanicOptions ?? []}
                onChange={handleMechanicChange(index)}
                menuInPortal
                data-testid={suffixTestId(`mechanic-[${index}]`, props)}
              />
            </Box>
            <Box flex={1}>
              <HStack spacing={4}>
                <Box flex={1}>
                  <FormField
                    control={props.control}
                    name={`assignMechanics.${index}.amount`}
                    type="number"
                    decimalPlaces={2}
                    label={index === 0 ? i18n.t('entity.order.itemQuantity') : undefined}
                    onChange={handleQuantityChange(index)}
                    isDisabled={fields.length === 1}
                    data-testid={suffixTestId(`quantity-[${index}]`, props)}
                  />
                </Box>
                <Box flex={1}>
                  <FormField
                    control={props.control}
                    name={`assignMechanics.${index}.ratio`}
                    type="integer"
                    label={index === 0 ? i18n.t('entity.orderRequest.labels.ratio') : undefined}
                    onChange={handleRatioChange(index)}
                    isDisabled={fields.length === 1}
                    data-testid={suffixTestId(`ratio-[${index}]`, props)}
                  />
                </Box>
                <Show when={fields.length > 1}>
                  <VStack>
                    <Space vertical={index === 0 ? 5 : 0} />
                    <IconButton
                      icon="content/clear"
                      onClick={() => remove(index)}
                      priority="tertiary"
                      severity="danger"
                      isDisabled={props.isDisabled}
                      data-testid={suffixTestId(`remove-[${index}]`, props)}
                    />
                  </VStack>
                </Show>
              </HStack>
            </Box>
          </HStack>
        </VStack>
      ))}
      <Button
        variant="link"
        title={i18n.t('entity.order.actions.addMechanic')}
        onClick={() => append({id: null, amount: 0, ratio: 0})}
        data-testid={suffixTestId('add', props)}
        isDisabled={props.isDisabled}
      />
    </VStack>
  );
}
